import { Fragment, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { FieldError } from "@shared-core/domain/object-ui/contract";
import {
  nestedTablePath,
  type SelectedRows,
} from "@shared-core/domain/object-ui/table-rows";
import { formatSchemaValue } from "@shared-core/domain/object-ui/values";
import type {
  FieldDescriptor,
  NestedTableDescriptor,
  TableColumn,
} from "@shared-core/domain/object-ui/widgets";
import { Button } from "../../primitives/button";
import { cn } from "../../primitives/utils";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { fieldId, FieldFrame, invalidProps, MoneyInput } from "./CommandFields";

/** A date column reads left to right like text; the others keep to the right. */
const readsLeft = (column: TableColumn) =>
  column.kind === "text" || column.kind === "date";

/** What a date input holds: the ISO day, or empty for none. */
const asDateText = (value: unknown) => (typeof value === "string" ? value : "");

type TableFieldDescriptor = Extract<FieldDescriptor, { kind: "table" }>;

type Row = Record<string, unknown>;

interface TableHandlers {
  /** Ticked rows, per table path. */
  selectedRows: SelectedRows;
  /** Errors on this field's path and below. */
  errors: FieldError[];
  disabled: boolean;
  /** A cell's full body path (`lines.1.destinations.0.quantity`). */
  onCellChange: (
    path: string,
    value: unknown,
    options?: { debounce?: boolean },
  ) => void;
  /** A table's body path (`lines`, `lines.1.destinations`) and the row ticked. */
  onRowSelected: (tablePath: string, row: number, isSelected: boolean) => void;
}

/**
 * The table input (brief §9.3): read-only columns, an editable cell per row
 * (a box to tick for a yes/no column), optional row ticks (unticked rows are left out of the command), a per-row
 * limit read from the row itself, and a link that fills a cell to it. Cell
 * errors arrive on row paths (`lines.2.amount`).
 *
 * A table whose rows the person adds themselves (`add_rows`: a command's request lines) is drawn as one block per
 * row, a labelled entry box for each column, with Remove on the row and the control that adds a row below.
 *
 * A row may hold a table of its own (`lines[].destinations[]`): it is drawn as
 * a sub-table under its parent row, with the same ticks, limits, fill links and
 * errors. One level of nesting is the limit; `widgets.ts` refuses deeper.
 * A sub-table, not cells spanning grouped rows, because its columns differ from
 * the parent's and a screen reader gets a table of its own to move through,
 * named for the row it belongs to.
 */
export function TableField({
  field,
  rows,
  rowsShown = null,
  ...handlers
}: {
  field: TableFieldDescriptor;
  rows: Row[];
  /** The owner's `field_hints[path].rows_shown`: the row keys to show first, the rest behind "Show all"; null shows all. */
  rowsShown?: readonly string[] | null;
} & TableHandlers) {
  const id = fieldId(field.path);
  const tableErrors = handlers.errors.filter((e) => e.field === field.path);
  return (
    <FieldFrame
      id={id}
      label={field.label}
      required={field.required}
      errors={tableErrors}
      asGroup
    >
      <div role="group" aria-labelledby={`${id}-label`}>
        {field.table.addLabel !== null ? (
          <EntryRows
            path={field.path}
            columns={field.table.columns}
            blankRow={field.table.blankRow}
            addLabel={field.table.addLabel}
            label={field.label}
            rows={rows}
            rowsShown={rowsShown}
            errors={handlers.errors}
            disabled={handlers.disabled}
            onRowsChange={(next) => handlers.onCellChange(field.path, next)}
            onCellChange={handlers.onCellChange}
          />
        ) : (
          <RowsTable
            path={field.path}
            columns={field.table.columns}
            isSelectable={field.table.selectable}
            nested={field.table.nested}
            rows={rows}
            parentLabel={null}
            {...handlers}
          />
        )}
      </div>
    </FieldFrame>
  );
}

/** One table's rows: the top-level table, or the sub-table of one parent row. */
function RowsTable({
  path,
  columns,
  isSelectable,
  nested,
  rows,
  parentLabel,
  selectedRows,
  errors,
  disabled,
  onCellChange,
  onRowSelected,
}: {
  path: string;
  columns: TableColumn[];
  isSelectable: boolean;
  /** Tables held in each row; empty for a sub-table (one level only). */
  nested: NestedTableDescriptor[];
  rows: Row[];
  /** The parent row's label, for a sub-table. */
  parentLabel: string | null;
} & TableHandlers) {
  const ticked = selectedRows[path] ?? [];
  const cellErrors = (row: number, column: string) =>
    errors.filter((e) => e.field === `${path}.${row}.${column}`);
  const rowErrors = (row: number) =>
    errors.filter((e) => e.field === `${path}.${row}`);
  const columnCount = columns.length + (isSelectable ? 1 : 0);

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            {isSelectable && (
              <TableHead className="w-8">
                <span className="sr-only">Include</span>
              </TableHead>
            )}
            {columns.map((column) => (
              <TableHead
                key={column.field}
                className={readsLeft(column) ? undefined : "text-right"}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, n) => {
            const isRowSelected = !isSelectable || ticked.includes(n);
            const ownLabel = String(row[columns[0]?.field ?? ""] ?? n + 1);
            const rowLabel =
              parentLabel === null ? ownLabel : `${parentLabel}, ${ownLabel}`;
            return (
              <Fragment key={n}>
                <TableRow>
                  {isSelectable && (
                    <TableCell>
                      <Checkbox
                        aria-label={`Include ${rowLabel}`}
                        checked={isRowSelected}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          onRowSelected(path, n, checked === true)
                        }
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => {
                    if (!column.editable)
                      return (
                        <TableCell
                          key={column.field}
                          className={
                            readsLeft(column)
                              ? undefined
                              : "text-right tabular-nums"
                          }
                        >
                          {formatSchemaValue(row[column.field], column.schema)}
                        </TableCell>
                      );
                    const cellPath = `${path}.${n}.${column.field}`;
                    const cellId = fieldId(cellPath);
                    const errs = cellErrors(n, column.field);
                    const max = column.maxFrom
                      ? row[column.maxFrom]
                      : undefined;
                    // A text cell reads left to right and takes the room it needs; numbers keep to the right.
                    const isText = column.kind === "text" && !column.options;
                    const isLeft = isText || column.kind === "date";
                    return (
                      <TableCell
                        key={column.field}
                        className={isLeft ? undefined : "text-right"}
                      >
                        {isRowSelected ? (
                          <div
                            className={cn(
                              "flex flex-col gap-1",
                              isText
                                ? "items-stretch"
                                : isLeft
                                  ? "items-start"
                                  : "items-end",
                            )}
                          >
                            <div
                              className={cn(
                                "flex items-center gap-2",
                                isLeft ? "justify-start" : "justify-end",
                              )}
                            >
                              {column.options ? (
                                // A choice per row from fixed values (Decide waiting orders…'s decision).
                                <Select
                                  value={
                                    typeof row[column.field] === "string"
                                      ? (row[column.field] as string)
                                      : undefined
                                  }
                                  disabled={disabled}
                                  onValueChange={(next) =>
                                    onCellChange(cellPath, next)
                                  }
                                >
                                  <SelectTrigger
                                    id={cellId}
                                    className="w-44"
                                    aria-label={`${column.label}, ${rowLabel}`}
                                    {...invalidProps(cellId, errs)}
                                  >
                                    <SelectValue placeholder="Choose…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {column.options.map((option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : column.kind === "boolean" ? (
                                // A per-row tick the person makes (Release's "the file and the brief match").
                                <Checkbox
                                  id={cellId}
                                  aria-label={`${column.label}, ${rowLabel}`}
                                  checked={row[column.field] === true}
                                  disabled={disabled}
                                  {...invalidProps(cellId, errs)}
                                  onCheckedChange={(checked) =>
                                    onCellChange(cellPath, checked === true)
                                  }
                                />
                              ) : column.kind === "date" ? (
                                <Input
                                  id={cellId}
                                  type="date"
                                  className="w-40"
                                  aria-label={`${column.label}, ${rowLabel}`}
                                  value={asDateText(row[column.field])}
                                  disabled={disabled}
                                  {...invalidProps(cellId, errs)}
                                  onChange={(e) =>
                                    onCellChange(
                                      cellPath,
                                      e.target.value || null,
                                    )
                                  }
                                />
                              ) : column.kind === "money" ? (
                                <MoneyInput
                                  id={cellId}
                                  className="max-w-36"
                                  ariaLabel={`${column.label}, ${rowLabel}`}
                                  value={row[column.field]}
                                  disabled={disabled}
                                  errors={errs}
                                  onChange={(value, options) =>
                                    onCellChange(cellPath, value, options)
                                  }
                                />
                              ) : (
                                <Input
                                  id={cellId}
                                  type={
                                    column.kind === "number" ? "number" : "text"
                                  }
                                  className={
                                    isText
                                      ? "w-full min-w-48"
                                      : "max-w-28 text-right tabular-nums"
                                  }
                                  aria-label={`${column.label}, ${rowLabel}`}
                                  value={
                                    row[column.field] == null
                                      ? ""
                                      : String(row[column.field])
                                  }
                                  disabled={disabled}
                                  {...invalidProps(cellId, errs)}
                                  onChange={(e) =>
                                    onCellChange(
                                      cellPath,
                                      column.kind === "number"
                                        ? e.target.value === ""
                                          ? null
                                          : Number(e.target.value)
                                        : e.target.value,
                                      { debounce: true },
                                    )
                                  }
                                />
                              )}
                              {typeof max === "number" && !disabled && (
                                <button
                                  type="button"
                                  className="whitespace-nowrap text-xs text-primary underline"
                                  aria-label={`${column.fillLabel}, ${rowLabel}`}
                                  onClick={() => onCellChange(cellPath, max)}
                                >
                                  {column.fillLabel}
                                </button>
                              )}
                            </div>
                            {errs.length > 0 && (
                              <p
                                id={`${cellId}-error`}
                                className="text-sm text-red-600"
                              >
                                {errs.map((e) => e.message).join(" ")}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Tick to include
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {nested.map((sub) => {
                  const subPath = nestedTablePath(path, n, sub.field);
                  const subRows = Array.isArray(row[sub.field])
                    ? (row[sub.field] as Row[])
                    : [];
                  const subTableErrors = errors.filter(
                    (e) => e.field === subPath,
                  );
                  return (
                    <TableRow key={sub.field} className="hover:bg-transparent">
                      <TableCell colSpan={columnCount} className="pt-0 pl-8">
                        <div
                          role="group"
                          aria-label={`${sub.label}, ${rowLabel}`}
                          className="space-y-1 border-l-2 border-border pl-3"
                        >
                          <p
                            className="text-xs font-medium text-muted-foreground"
                            aria-hidden
                          >
                            {sub.label}
                          </p>
                          {isRowSelected ? (
                            <RowsTable
                              path={subPath}
                              columns={sub.columns}
                              isSelectable={sub.selectable}
                              nested={[]}
                              rows={subRows}
                              parentLabel={rowLabel}
                              selectedRows={selectedRows}
                              errors={errors}
                              disabled={disabled}
                              onCellChange={onCellChange}
                              onRowSelected={onRowSelected}
                            />
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Tick to include
                            </p>
                          )}
                          {subTableErrors.map((e) => (
                            <p key={e.code} className="text-sm text-red-600">
                              {e.message}
                            </p>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columnCount}
                className="text-sm text-muted-foreground"
              >
                No rows.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {rows.map((_, n) =>
        rowErrors(n).map((e) => (
          <p key={`${n}-${e.code}`} className="text-sm text-red-600">
            {e.message}
          </p>
        )),
      )}
    </>
  );
}

/**
 * The rows the person adds and removes: a block per row with a labelled entry box for each column, whose cell errors
 * arrive on row paths (`lines.2.quantity`) and show under the box.
 */
function EntryRows({
  path,
  columns,
  blankRow,
  addLabel,
  label,
  rows,
  rowsShown,
  errors,
  disabled,
  onRowsChange,
  onCellChange,
}: {
  path: string;
  columns: TableColumn[];
  blankRow: Row;
  addLabel: string;
  /** The field's label ("Lines"), for the words about rows left behind "Show all". */
  label: string;
  rows: Row[];
  rowsShown: readonly string[] | null;
  errors: FieldError[];
  disabled: boolean;
  onRowsChange: (rows: Row[]) => void;
  onCellChange: TableHandlers["onCellChange"];
}) {
  // A read-only column is the row's own key (an invoice's line number): a block is headed by it, so the headings
  // still match the record's lines after one is removed, and a row the person adds has none yet.
  const keyColumn = columns.find((column) => !column.editable);
  const headingOf = (row: Row, n: number) => {
    if (!keyColumn) return `Line ${n + 1}`;
    const key = row[keyColumn.field];
    return key == null || key === "" ? "New line" : `Line ${String(key)}`;
  };
  // Only the rows the owner says to show first, until the person asks for the rest. A row with no key yet (one just
  // added) is always shown, and so is any row an error is on: the person must be able to see what to put right.
  const [isAllShown, setAllShown] = useState(false);
  const isHidden = (row: Row, n: number) => {
    if (rowsShown === null || isAllShown || !keyColumn) return false;
    const key = row[keyColumn.field];
    return (
      key != null &&
      key !== "" &&
      !rowsShown.includes(String(key)) &&
      !errors.some((e) => e.field.startsWith(`${path}.${n}.`))
    );
  };
  const shown = rows
    .map((row, n) => ({ row, n }))
    .filter(({ row, n }) => !isHidden(row, n));
  return (
    <div className="space-y-3">
      {shown.map(({ row, n }) => (
        <fieldset
          key={n}
          aria-label={headingOf(row, n)}
          className="space-y-2 rounded-md border border-border p-3"
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold" aria-hidden>
              {headingOf(row, n)}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              aria-label={`Remove ${headingOf(row, n).toLowerCase()}`}
              onClick={() => onRowsChange(rows.filter((_, i) => i !== n))}
            >
              <Trash2 aria-hidden />
              Remove
            </Button>
          </div>
          {/* As many boxes to a line as the dialog is wide, so a wide dialog's lines stay short. */}
          <div className="grid gap-3 sm:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
            {columns
              .filter((column) => column.editable)
              .map((column) => {
                const cellPath = `${path}.${n}.${column.field}`;
                const cellId = fieldId(cellPath);
                const errs = errors.filter((e) => e.field === cellPath);
                return (
                  <div key={column.field} className="space-y-1">
                    <Label htmlFor={cellId}>{column.label}</Label>
                    {column.options ? (
                      // A column of fixed values is a choice, as in a table of ticked rows (Set rules…'s attribute).
                      <Select
                        value={
                          typeof row[column.field] === "string" &&
                          row[column.field] !== ""
                            ? (row[column.field] as string)
                            : undefined
                        }
                        disabled={disabled}
                        onValueChange={(next) => onCellChange(cellPath, next)}
                      >
                        <SelectTrigger
                          id={cellId}
                          className="w-full"
                          {...invalidProps(cellId, errs)}
                        >
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {column.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : column.kind === "date" ? (
                      <Input
                        id={cellId}
                        type="date"
                        value={asDateText(row[column.field])}
                        disabled={disabled}
                        {...invalidProps(cellId, errs)}
                        onChange={(e) =>
                          onCellChange(cellPath, e.target.value || null)
                        }
                      />
                    ) : column.kind === "money" ? (
                      <MoneyInput
                        id={cellId}
                        value={row[column.field]}
                        disabled={disabled}
                        errors={errs}
                        onChange={(value, options) =>
                          onCellChange(cellPath, value, options)
                        }
                      />
                    ) : (
                      <Input
                        id={cellId}
                        type={column.kind === "number" ? "number" : "text"}
                        value={
                          row[column.field] == null
                            ? ""
                            : String(row[column.field])
                        }
                        disabled={disabled}
                        {...invalidProps(cellId, errs)}
                        onChange={(e) =>
                          onCellChange(
                            cellPath,
                            column.kind === "number"
                              ? e.target.value === ""
                                ? null
                                : Number(e.target.value)
                              : e.target.value,
                            { debounce: true },
                          )
                        }
                      />
                    )}
                    {column.schema.description && (
                      <p className="text-xs text-muted-foreground">
                        {column.schema.description}
                      </p>
                    )}
                    {errs.length > 0 && (
                      <p
                        id={`${cellId}-error`}
                        className="text-sm text-red-600"
                      >
                        {errs.map((e) => e.message).join(" ")}
                      </p>
                    )}
                  </div>
                );
              })}
          </div>
        </fieldset>
      ))}
      {shown.length < rows.length && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <p className="text-muted-foreground">
            {shown.length} of {rows.length} {label.toLowerCase()} shown.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAllShown(true)}
          >
            Show all {label.toLowerCase()}
          </Button>
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onRowsChange([...rows, { ...blankRow }])}
      >
        <Plus aria-hidden />
        {addLabel}
      </Button>
    </div>
  );
}
